import { Card } from '@portal/models/bank';
import {
  CardFirstAccessStatus,
  CardStage,
  CardStatusDock,
  CardType,
} from '@portal/models/enumerators/cards';
import { translate } from './i18n';

export const BLOCKED_STATUS_DOCK = [
  CardStatusDock.BLOCKED,
  CardStatusDock.CANCELED,
  CardStatusDock.LOCKED_INCORRECT_PASSWORD,
  CardStatusDock.BLOCKED_PREVENTIVE,
] as const;

export type CardFeature =
  | 'cancel'
  | 'duplicate'
  | 'block'
  | 'unblock'
  | 'change_password'
  | 'tracking'
  | 'view_password'
  | 'limit';

interface ValidationResult {
  isValid: boolean;
  reason?:
    | 'CARD_PREPARING'
    | 'BLOCKED_OR_CANCELED'
    | 'NO_PERMISSION'
    | 'CARD_NOT_RECEIVED';
  messageKey?: string;
}

export const isPhysicalCard = (cardType?: CardType): boolean => {
  return cardType === CardType.PHYSICAL;
};

export const isVirtualCard = (cardType?: CardType): boolean => {
  return cardType === CardType.VIRTUAL;
};

export const isCardBlocked = (status?: CardStatusDock): boolean => {
  return [
    CardStatusDock.BLOCKED,
    CardStatusDock.LOCKED_INCORRECT_PASSWORD,
  ].includes(status as CardStatusDock);
};

export const isCardCanceled = (status?: CardStatusDock): boolean => {
  return status === CardStatusDock.CANCELED;
};

export const isCardBlockedOrCanceled = (status?: CardStatusDock): boolean => {
  if (!status) return false;
  return BLOCKED_STATUS_DOCK.includes(
    status as (typeof BLOCKED_STATUS_DOCK)[number]
  );
};

export const isCardActive = (status?: CardStatusDock): boolean => {
  return status === CardStatusDock.NORMAL;
};

export const isCardDelivered = (cardStage?: number): boolean => {
  return (cardStage ?? 0) >= CardStage.FORWARDED;
};

export const isCardReceivedByUser = (card?: Card | null): boolean => {
  if (!card) return false;
  return card.isReadyToUnlock === true;
};

export const canConfirmCardReceipt = (card?: Card | null): boolean => {
  if (!card) return false;

  const isPhysical = isPhysicalCard(card.type);
  const isDelivered = isCardDelivered(card.cardStage);
  const notReceivedYet = !card.isReadyToUnlock;

  return isPhysical && isDelivered && notReceivedYet;
};

export const isFirstAccessPending = (
  type: CardType | undefined,
  firstAccessStatus?: CardFirstAccessStatus | null
) => {
  return (
    type === CardType.PHYSICAL &&
    [CardFirstAccessStatus.PENDING, null, undefined].includes(firstAccessStatus)
  );
};

export const shouldRenderFirstAccess = (card?: Card | null): boolean => {
  if (!card) return false;

  if (!isPhysicalCard(card.type)) return false;

  return (
    isFirstAccessPending(card.type, card.firstAccessStatus) ||
    (isCardReceivedByUser(card) &&
      (card?.cardStage ?? 0) <= CardStage.FORWARDED)
  );
};

export const canUnblockByStage = (
  cardStage?: number,
  cardType?: CardType,
  isReadyToUnlock?: boolean
): boolean => {
  return (
    isCardDelivered(cardStage) &&
    isPhysicalCard(cardType) &&
    isReadyToUnlock === true
  );
};

export const validateCardFunctionality = (
  feature: CardFeature,
  card?: Card | null
): ValidationResult => {
  if (!card) {
    return { isValid: false };
  }

  const { status, type, firstAccessStatus } = card;
  const cardStage = card.cardStage ?? 0;
  const cardStatus = status as CardStatusDock;

  const isCanceled = isCardCanceled(cardStatus);
  const isBlockedPreventive = cardStatus === CardStatusDock.BLOCKED_PREVENTIVE;
  const isBlocked = isCardBlocked(cardStatus);
  const isBlockedOrCanceled = isCardBlockedOrCanceled(cardStatus);
  const isDelivered = isCardDelivered(cardStage);
  const isPhysical = isPhysicalCard(type);
  const isPending = isFirstAccessPending(type, firstAccessStatus);
  const isReceived = isCardReceivedByUser(card);

  if (isCanceled) {
    return {
      isValid: false,
      reason: 'BLOCKED_OR_CANCELED',
      messageKey: 'PAGES.CARDS.MY_CARDS.DETAILS.FUNCTIONS_UNAVAILABLE.MESSAGE',
    };
  }

  if (isBlockedPreventive && feature !== 'cancel') {
    return {
      isValid: false,
      reason: 'BLOCKED_OR_CANCELED',
      messageKey: 'PAGES.CARDS.MY_CARDS.DETAILS.FUNCTIONS_UNAVAILABLE.MESSAGE',
    };
  }

  if (feature === 'block') {
    if (isBlocked) {
      return {
        isValid: false,
        reason: 'BLOCKED_OR_CANCELED',
        messageKey:
          'PAGES.CARDS.MY_CARDS.DETAILS.FUNCTIONS_UNAVAILABLE.MESSAGE',
      };
    }
    return { isValid: true };
  }

  if (feature === 'unblock') {
    return { isValid: true };
  }

  if (isBlockedOrCanceled) {
    return {
      isValid: false,
      reason: 'BLOCKED_OR_CANCELED',
      messageKey: 'PAGES.CARDS.MY_CARDS.DETAILS.FUNCTIONS_UNAVAILABLE.MESSAGE',
    };
  }

  if (isPhysical && isPending) {
    if (!isReceived) {
      return {
        isValid: false,
        reason: 'CARD_NOT_RECEIVED',
        messageKey: 'PAGES.CARDS.MY_CARDS.DETAILS.CARD_NOT_RECEIVED',
      };
    }

    if (!isDelivered) {
      return {
        isValid: false,
        reason: 'CARD_PREPARING',
        messageKey: 'PAGES.CARDS.MY_CARDS.CARD_PREPARING',
      };
    }
  }

  return { isValid: true };
};

export const validatePassword = (password: string): ValidationResult => {
  const numericValue = password.replace(/\D/g, '');

  if (numericValue.length !== 4) {
    return {
      isValid: false,
      messageKey: 'PAGES.CARDS.MY_CARDS.DETAILS.CHANGE_PASSWORD.LENGTH_ERROR',
    };
  }

  const hasRepeated = numericValue
    .split('')
    .some((digit, index, array) => digit === array[index + 1]);

  if (hasRepeated) {
    return {
      isValid: false,
      messageKey: 'PAGES.CARDS.MY_CARDS.DETAILS.CHANGE_PASSWORD.REPEATED_ERROR',
    };
  }

  return { isValid: true };
};

export const sanitizePasswordInput = (value: string): string => {
  return value.replace(/\D/g, '').slice(0, 4);
};

export const formatExpirationDate = (expirationDate: string): string => {
  if (!expirationDate) return '';

  if (expirationDate.includes('/')) {
    return expirationDate;
  }

  if (expirationDate.length === 4) {
    return `${expirationDate.slice(0, 2)}/${expirationDate.slice(2)}`;
  }

  return expirationDate;
};

export const canShowCardData = (
  status?: CardStatusDock,
  isRevealing?: boolean
): boolean => {
  return !isRevealing && status !== CardStatusDock.BLOCKED;
};

export const handleUnavailableFunctionality = (feature: CardFeature) => {
  const messages: Record<CardFeature, string> = {
    cancel: 'PAGES.CARDS.MY_CARDS.DETAILS.FUNCTIONS_UNAVAILABLE.CANCEL',
    change_password:
      'PAGES.CARDS.MY_CARDS.DETAILS.FUNCTIONS_UNAVAILABLE.CHANGE_PASSWORD',
    view_password:
      'PAGES.CARDS.MY_CARDS.DETAILS.FUNCTIONS_UNAVAILABLE.VIEW_PASSWORD',
    duplicate:
      'PAGES.CARDS.MY_CARDS.DETAILS.FUNCTIONS_UNAVAILABLE.REQUEST_DUPLICATE',
    limit: 'PAGES.CARDS.MY_CARDS.DETAILS.FUNCTIONS_UNAVAILABLE.LIMIT',
    block: '',
    unblock: '',
    tracking: '',
  };

  return translate(messages[feature]);
};

export const validateCardPassword = (
  password: string
): { valid: boolean; error?: string } => {
  if (!/^\d{4}$/.test(password)) {
    return {
      valid: false,
      error: translate(
        'PAGES.CARDS.MY_CARDS.DETAILS.CHANGE_PASSWORD.MUST_BE_4_NUMERIC_DIGITS'
      ),
    };
  }

  if (/^(\d)\1{3}$/.test(password)) {
    return {
      valid: false,
      error: translate(
        'PAGES.CARDS.MY_CARDS.DETAILS.CHANGE_PASSWORD.NO_REPEATED_NUMBERS'
      ),
    };
  }

  const digits = password.split('').map(Number);

  const isSequentialAsc = digits.every(
    (n, i) => i === 0 || n === digits[i - 1] + 1
  );
  const isSequentialDesc = digits.every(
    (n, i) => i === 0 || n === digits[i - 1] - 1
  );

  if (isSequentialAsc || isSequentialDesc) {
    return {
      valid: false,
      error:
        'PAGES.CARDS.MY_CARDS.DETAILS.CHANGE_PASSWORD.NO_SEQUENTIAL_NUMBERS',
    };
  }

  if (password.slice(0, 2) === password.slice(2, 4)) {
    return {
      valid: false,
      error:
        'PAGES.CARDS.MY_CARDS.DETAILS.CHANGE_PASSWORD.NO_REPEATED_PATTERNS',
    };
  }

  if (digits[0] === digits[1] && digits[2] === digits[3]) {
    return {
      valid: false,
      error: 'PAGES.CARDS.MY_CARDS.DETAILS.CHANGE_PASSWORD.NO_REPEATED_PAIRS',
    };
  }

  return { valid: true };
};
